import apiService from './apiService';
import { getProjectionConfidence } from '../../types/boxOfficeTypes';

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const CONCURRENCY = 4;

const summaryCache = new Map();

const getCachedSummary = (imdbId) => {
  const entry = summaryCache.get(imdbId);
  if (!entry) return null;
  const { timestamp, summary } = entry;
  if (Date.now() - timestamp > CACHE_TTL_MS) {
    summaryCache.delete(imdbId);
    return null;
  }
  return summary;
};

const setCachedSummary = (imdbId, summary) => {
  summaryCache.set(imdbId, {
    summary,
    timestamp: Date.now()
  });
};

const normaliseProjection = (projection) => {
  if (!projection) return null;

  const {
    actualProjection,
    projectionFloor,
    projectionCeiling,
    outlierMinimum,
    outlierCeiling,
    projectionDate,
    updatedAt,
    createdAt,
    notes
  } = projection;

  const confidence = getProjectionConfidence(actualProjection, outlierMinimum, outlierCeiling);

  return {
    actualProjection,
    projectionFloor,
    projectionCeiling,
    outlierMinimum,
    outlierCeiling,
    projectionDate,
    updatedAt,
    createdAt,
    notes,
    confidence
  };
};

const fetchProjectionSummary = async (imdbId) => {
  try {
    const response = await apiService.getFilmBoxOfficeProjections(imdbId, {
      limit: 1,
      sortBy: 'projection_date',
      sortOrder: 'desc',
      isActive: true
    });

    const projections = response?.data?.projections || [];
    const latest = projections[0];
    const summary = normaliseProjection(latest);
    setCachedSummary(imdbId, summary);
    return summary;
  } catch (error) {
    console.error('❌ Failed to fetch box office projection summary:', {
      imdbId,
      error: error?.message || error
    });
    setCachedSummary(imdbId, null);
    return null;
  }
};

const runWithConcurrency = async (items, worker, concurrency = CONCURRENCY) => {
  const queue = [...items];
  const results = {};

  const executeBatch = async () => {
    if (queue.length === 0) return;
    const batch = queue.splice(0, concurrency);
    await Promise.all(
      batch.map(async (item) => {
        results[item] = await worker(item);
      })
    );
    await executeBatch();
  };

  await executeBatch();
  return results;
};

export const getProjectionSummaries = async (imdbIds, options = {}) => {
  if (!Array.isArray(imdbIds) || imdbIds.length === 0) {
    return {};
  }

  const uniqueIds = Array.from(new Set(imdbIds.filter(Boolean)));

  const summaries = {};
  const idsToFetch = [];

  uniqueIds.forEach((id) => {
    if (!options.forceRefresh) {
      const cached = getCachedSummary(id);
      if (cached !== null) {
        summaries[id] = cached;
        return;
      }
    }
    idsToFetch.push(id);
  });

  if (idsToFetch.length > 0) {
    const fetched = await runWithConcurrency(idsToFetch, fetchProjectionSummary, options.concurrency || CONCURRENCY);
    Object.assign(summaries, fetched);
  }

  return summaries;
};

export const getProjectionSummary = async (imdbId, options = {}) => {
  if (!imdbId) return null;

  if (!options.forceRefresh) {
    const cached = getCachedSummary(imdbId);
    if (cached !== null) {
      return cached;
    }
  }

  return await fetchProjectionSummary(imdbId);
};

export default {
  getProjectionSummaries,
  getProjectionSummary
};

